import { Control } from "../type";
import {
  FIELD_NAMES,
  VALIDATION_PATTERNS,
  VALIDATION_MESSAGES,
} from "../shared/constants/formValidationConstants";

// ─── Internal helpers ────────────────────────────────────────────────────────

interface GPAContext {
  userGPAControl: Control | undefined;
  gpaScaleControl: Control | undefined;
  gpaValueStr: string;
  hasGPAValue: boolean;
}

interface ClassRankContext {
  classRankControl: Control | undefined;
  classRankOutOfControl: Control | undefined;
  classRankValueStr: string;
  classRankOutOfValueStr: string;
  hasClassRank: boolean;
  hasClassRankOutOf: boolean;
}

const getGPAContext = (
  attributes: Control[],
  formValues: Record<string, string>,
): GPAContext => {
  const userGPAControl = attributes.find(
    (attr) =>
      attr.name?.toLowerCase().includes("your high school gpa") ||
      attr.name?.toLowerCase().includes("high school gpa") ||
      (attr as any).friendlyName?.toLowerCase().includes("userinputgpa"),
  );
  const gpaScaleControl = attributes.find(
    (attr) =>
      attr.name?.toLowerCase().includes("gpa out of") ||
      (attr as any).friendlyName?.toLowerCase().includes("gpaoutof") ||
      attr.name?.toLowerCase().includes("gpa scale"),
  );
  const gpaValueStr = formValues[userGPAControl?._id || ""] || "";
  return {
    userGPAControl,
    gpaScaleControl,
    gpaValueStr,
    hasGPAValue: gpaValueStr.trim() !== "",
  };
};

const getClassRankContext = (
  attributes: Control[],
  formValues: Record<string, string>,
): ClassRankContext => {
  const classRankControl = attributes.find(
    (attr) =>
      attr.name?.toLowerCase().includes("your class rank") ||
      (attr as any).friendlyName?.toLowerCase().includes("your class rank"),
  );
  const classRankOutOfControl = attributes.find(
    (attr) =>
      attr.name?.toLowerCase().includes("class rank out of") ||
      (attr as any).friendlyName?.toLowerCase().includes("class rank out of"),
  );
  const classRankValueStr = formValues[classRankControl?._id || ""] || "";
  const classRankOutOfValueStr =
    formValues[classRankOutOfControl?._id || ""] || "";
  return {
    classRankControl,
    classRankOutOfControl,
    classRankValueStr,
    classRankOutOfValueStr,
    hasClassRank: classRankValueStr.trim() !== "",
    hasClassRankOutOf: classRankOutOfValueStr.trim() !== "",
  };
};

/** Returns true when the required-field check should be skipped for the control. */
const shouldSkipRequired = (
  control: Control,
  gpaScaleControl: Control | undefined,
  hasGPAValue: boolean,
  classRankOutOfControl: Control | undefined,
  hasClassRank: boolean,
): boolean => {
  if (control._id === gpaScaleControl?._id && !hasGPAValue) return true;
  if (control._id === classRankOutOfControl?._id && !hasClassRank) return true;
  return false;
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Validates form values against a set of control definitions.
 * Currently, it checks for required fields and email format validation.
 *
 * @param attributes - An array of control definitions.
 * @param formValues - The current values of the form.
 * @returns A record of error messages, keyed by control ID.
 */
export const validateForm = (
  attributes: Control[],
  formValues: Record<string, string>,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const { userGPAControl, gpaScaleControl, gpaValueStr, hasGPAValue } =
    getGPAContext(attributes, formValues);

  const {
    classRankControl,
    classRankOutOfControl,
    classRankValueStr,
    classRankOutOfValueStr,
    hasClassRank,
    hasClassRankOutOf,
  } = getClassRankContext(attributes, formValues);

  for (const control of attributes) {
    const value = formValues[control._id];
    const fieldName = control.displayName || control.name;

    // Check required fields
    if (control.isRequired) {
      if (
        shouldSkipRequired(
          control,
          gpaScaleControl,
          hasGPAValue,
          classRankOutOfControl,
          hasClassRank,
        )
      ) {
        continue;
      }
      if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
      ) {
        errors[control._id] = VALIDATION_MESSAGES.REQUIRED(fieldName);
      }
    }

    // Check email validation for Email Address fields
    if (
      fieldName === FIELD_NAMES.EMAIL_ADDRESS &&
      value &&
      String(value).trim() !== ""
    ) {
      if (!VALIDATION_PATTERNS.EMAIL.test(String(value).trim())) {
        errors[control._id] = VALIDATION_MESSAGES.INVALID_EMAIL;
      }
    }
  }

  // GPA cross-field validation: Check if "Your High School GPA" exceeds "GPA out of" value
  if (userGPAControl && gpaScaleControl && hasGPAValue) {
    const gpaScaleValueStr = formValues[gpaScaleControl._id] || "";
    const gpaValue = Number.parseFloat(gpaValueStr); // Fixed: Declare gpaValue in correct scope
    const gpaScaleValue = Number.parseFloat(gpaScaleValueStr);

    // Check if GPA scale is empty when GPA is provided
    if (gpaScaleValueStr.trim() === "" || Number.isNaN(gpaScaleValue)) {
      errors[gpaScaleControl._id] = "This field is required";
    }
    // Validate GPA scale range (dynamic based on API response) - separate validation
    else if (!Number.isNaN(gpaScaleValue)) {
      // Only validate range if API provides min/max values
      if (
        gpaScaleControl.possibleMinValue !== undefined &&
        gpaScaleControl.possibleMaxValue !== undefined
      ) {
        if (gpaScaleValue < gpaScaleControl.possibleMinValue) {
          errors[gpaScaleControl._id] =
            `GPA scale must be at least ${gpaScaleControl.possibleMinValue.toFixed(2)}`;
        } else if (gpaScaleValue > gpaScaleControl.possibleMaxValue) {
          errors[gpaScaleControl._id] =
            `GPA scale cannot exceed ${gpaScaleControl.possibleMaxValue.toFixed(2)}`;
        }
      }
    }

    // Separate validation: Check if GPA exceeds GPA scale (always run this check if values are valid)
    if (!Number.isNaN(gpaValue) && !Number.isNaN(gpaScaleValue)) {
      if (gpaValue > gpaScaleValue) {
        errors[userGPAControl._id] =
          "The GPA cannot be higher than the GPA scale. If you have a weighted GPA higher than the scale, please enter the highest GPA on your high school's scale.";
      }
      // Validate GPA is positive when provided
      else if (!Number.isNaN(gpaValue) && gpaValue <= 0) {
        errors[userGPAControl._id] = "GPA must be greater than 0";
      }
    }
  }

  // Class Rank cross-field validation: Both fields must be filled together
  if (classRankControl && classRankOutOfControl) {
    // Check if only one field is filled
    if (hasClassRank !== hasClassRankOutOf) {
      if (hasClassRank && !hasClassRankOutOf) {
        errors[classRankOutOfControl._id] = "Please enter a value";
      } else if (!hasClassRank && hasClassRankOutOf) {
        errors[classRankControl._id] = "Please enter a value";
      }
    }

    // Check if class rank is greater than class size
    if (hasClassRank && hasClassRankOutOf) {
      const classRankValue = Number.parseInt(classRankValueStr, 10);
      const classRankOutOfValue = Number.parseInt(classRankOutOfValueStr, 10);

      if (!Number.isNaN(classRankValue) && !Number.isNaN(classRankOutOfValue)) {
        if (classRankValue > classRankOutOfValue) {
          errors[classRankControl._id] =
            "Your class rank cannot be more than the total number of students in your class";
        }
        // Validate both are positive
        if (classRankValue <= 0) {
          errors[classRankControl._id] = "Class rank must be greater than 0";
        }
        if (classRankOutOfValue <= 0) {
          errors[classRankOutOfControl._id] =
            "Class size must be greater than 0";
        }
      }
    }
  }

  return errors;
};

/**
 * Checks if a form section is valid based on its required fields.
 * This function does not generate error messages and is optimized for performance.
 *
 * @param attributes - An array of control definitions for the section.
 * @param formValues - The current values of the form.
 * @returns A boolean indicating if all required fields are filled.
 */
export const isFormSectionValid = (
  attributes: Control[],
  formValues: Record<string, string>,
): boolean => {
  const { gpaScaleControl, hasGPAValue } = getGPAContext(
    attributes,
    formValues,
  );
  const { classRankOutOfControl, hasClassRank } = getClassRankContext(
    attributes,
    formValues,
  );

  for (const control of attributes) {
    if (control.isRequired) {
      if (
        shouldSkipRequired(
          control,
          gpaScaleControl,
          hasGPAValue,
          classRankOutOfControl,
          hasClassRank,
        )
      ) {
        continue;
      }
      const value = formValues[control._id];
      if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
      ) {
        return false; // Found an empty required field, section is invalid.
      }
    }
  }
  return true; // All required fields are filled.
};
